import React from 'react';
import { View, Text, Pressable, TextInput, KeyboardAvoidingView, Keyboard } from 'react-native';
import { isIOS } from '@/lib/platform';
import { useKeyboardVisible } from '@/lib/useKeyboardVisible';
import AnimatedPressable from '@/components/AnimatedPressable';
import {
  ThumbsUp,
  ThumbsDown,
  Clock,
  CheckCircle,
  X,
  DollarSign,
  TrendingUp,
} from 'lucide-react-native';
import Animated, { FadeIn, FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatCurrency } from '@/lib/utils/format';

type DecisionType = 'invest' | 'pass' | 'follow_up';

// ─── ConclusionPanel ────────────────────────────────────────────
interface ConclusionPanelProps {
  visible: boolean;
  onClose: () => void;
  decision: DecisionType | null;
  onSetDecision: (d: DecisionType) => void;
  finalComments: string;
  onSetFinalComments: (v: string) => void;
  onSubmit: () => void;
}

export function ConclusionPanel({
  visible,
  onClose,
  decision,
  onSetDecision,
  finalComments,
  onSetFinalComments,
  onSubmit,
}: ConclusionPanelProps) {
  const colors = useThemeColors();
  const keyboardVisible = useKeyboardVisible();

  if (!visible) return null;

  return (
    <Animated.View
      entering={FadeIn}
      className="absolute inset-0"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      {/* First tap dismisses the keyboard and keeps the sheet; only a second
          tap closes. These sheets hold free-text commitment notes and a
          conclusion rationale — closing on the first tap discarded them. */}
      <Pressable
        className="flex-1"
        accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
        onPress={() => {
          if (keyboardVisible) {
            Keyboard.dismiss();
            return;
          }
          onClose();
        }}
      />
      <KeyboardAvoidingView behavior={isIOS ? 'padding' : 'height'}>
      <Animated.View
        entering={FadeInUp.springify()}
        className="rounded-t-3xl p-5"
        style={{ backgroundColor: colors.surface.primary }}
      >
        <Text style={{ color: colors.text.primary }} className="text-xl font-bold mb-4">Close Due Diligence</Text>

        <View className="gap-3 mb-4">
          {([
            { key: 'invest' as DecisionType, label: 'Invest', icon: ThumbsUp, color: colors.success, bg: colors.traffic.greenLight },
            { key: 'follow_up' as DecisionType, label: 'Follow Up', icon: Clock, color: colors.warning, bg: colors.amber.light },
            { key: 'pass' as DecisionType, label: 'Pass', icon: ThumbsDown, color: colors.error, bg: colors.red.light },
          ]).map((opt) => (
            <AnimatedPressable
              key={opt.key}
              onPress={() => onSetDecision(opt.key)}
              className="flex-row items-center gap-3 p-4 rounded-xl"
              style={{ backgroundColor: decision === opt.key ? opt.bg : colors.surface.secondary, borderWidth: decision === opt.key ? 2 : 0, borderColor: opt.color }}
            >
              <opt.icon size={24} color={opt.color} />
              <Text style={{ color: colors.text.primary }} className="font-medium">{opt.label}</Text>
              {decision === opt.key && <CheckCircle size={20} color={opt.color} style={{ marginLeft: 'auto' }} />}
            </AnimatedPressable>
          ))}
        </View>

        <TextInput
          className="rounded-xl p-3 text-sm mb-4"
          style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, minHeight: 100 }}
          placeholder="Add your final comments..."
          placeholderTextColor={colors.text.tertiary}
          value={finalComments}
          onChangeText={onSetFinalComments}
          multiline
          textAlignVertical="top"
        />

        <AnimatedPressable
          onPress={onSubmit}
          disabled={!decision || !finalComments.trim()}
          className="py-4 rounded-xl items-center"
          style={{ backgroundColor: decision && finalComments.trim() ? colors.primary[600] : colors.surface.tertiary }}
        >
          <Text className="font-semibold" style={{ color: decision && finalComments.trim() ? 'white' : colors.text.tertiary }}>Submit Conclusion</Text>
        </AnimatedPressable>
      </Animated.View>
      </KeyboardAvoidingView>
    </Animated.View>
  );
}

// ─── SoftCommitPanel ────────────────────────────────────────────
interface SoftCommitPanelProps {
  visible: boolean;
  onClose: () => void;
  startupName: string;
  commitmentAmount: string;
  onSetCommitmentAmount: (v: string) => void;
  commitmentNotes: string;
  onSetCommitmentNotes: (v: string) => void;
  onSubmit: () => void;
}

export function SoftCommitPanel({
  visible,
  onClose,
  startupName,
  commitmentAmount,
  onSetCommitmentAmount,
  commitmentNotes,
  onSetCommitmentNotes,
  onSubmit,
}: SoftCommitPanelProps) {
  const colors = useThemeColors();
  const keyboardVisible = useKeyboardVisible();
  const amountValid = commitmentAmount && parseFloat(commitmentAmount) > 0;

  if (!visible) return null;

  return (
    <Animated.View
      entering={FadeIn}
      className="absolute inset-0"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      {/* First tap dismisses the keyboard and keeps the sheet; only a second
          tap closes. These sheets hold free-text commitment notes and a
          conclusion rationale — closing on the first tap discarded them. */}
      <Pressable
        className="flex-1"
        accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
        onPress={() => {
          if (keyboardVisible) {
            Keyboard.dismiss();
            return;
          }
          onClose();
        }}
      />
      <KeyboardAvoidingView behavior={isIOS ? 'padding' : 'height'}>
      <Animated.View
        entering={FadeInUp.springify()}
        className="rounded-t-3xl p-5"
        style={{ backgroundColor: colors.surface.primary }}
      >
        <View className="flex-row items-center justify-between mb-4">
          <Text style={{ color: colors.text.primary }} className="text-xl font-bold">Signal Interest</Text>
          <Pressable onPress={onClose} className="w-8 h-8 rounded-full items-center justify-center" style={{ backgroundColor: colors.surface.secondary }}>
            <X size={18} color={colors.text.secondary} />
          </Pressable>
        </View>

        <Text style={{ color: colors.text.secondary }} className="text-sm mb-4">
          Indicate your investment interest in {startupName}.
        </Text>

        {/* Amount Input */}
        <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Commitment Amount</Text>
        <View className="flex-row items-center gap-2 mb-4">
          <View className="flex-row items-center flex-1 rounded-xl px-4" style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}>
            <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mr-1">$</Text>
            <TextInput
              className="flex-1 py-3 text-lg"
              style={{ color: colors.text.primary }}
              value={commitmentAmount}
              onChangeText={onSetCommitmentAmount}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor={colors.text.tertiary}
            />
            <Text style={{ color: colors.text.tertiary }} className="text-base font-medium">K</Text>
          </View>
        </View>

        {/* Notes Input */}
        <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Notes (optional)</Text>
        <TextInput
          className="rounded-xl p-3 text-sm mb-4"
          style={{ backgroundColor: colors.surface.secondary, color: colors.text.primary, minHeight: 70, borderWidth: 1, borderColor: colors.border.light }}
          placeholder="Any conditions or comments..."
          placeholderTextColor={colors.text.tertiary}
          value={commitmentNotes}
          onChangeText={onSetCommitmentNotes}
          multiline
          textAlignVertical="top"
        />

        {/* Disclaimer */}
        <View className="rounded-lg p-3 mb-4" style={{ backgroundColor: colors.amber.light }}>
          <Text style={{ color: colors.warning }} className="text-xs leading-4">
            Soft commitment during active DD. You can adjust or withdraw anytime.
          </Text>
        </View>

        <AnimatedPressable
          onPress={onSubmit}
          disabled={!amountValid}
          className="py-4 rounded-xl items-center"
          style={{ backgroundColor: amountValid ? colors.primary[600] : colors.surface.tertiary }}
        >
          <Text className="font-semibold" style={{ color: amountValid ? 'white' : colors.text.tertiary }}>
            Commit {amountValid ? formatCurrency(parseFloat(commitmentAmount) * 1000) : ''}
          </Text>
        </AnimatedPressable>
      </Animated.View>
      </KeyboardAvoidingView>
    </Animated.View>
  );
}

// ─── HardCommitPanel ────────────────────────────────────────────
interface HardCommitPanelProps {
  visible: boolean;
  onClose: () => void;
  hardCommitAmount: string;
  onSetHardCommitAmount: (v: string) => void;
  onSubmit: () => void;
  isConversion: boolean;
  previousAmount?: number;
}

export function HardCommitPanel({
  visible,
  onClose,
  hardCommitAmount,
  onSetHardCommitAmount,
  onSubmit,
  isConversion,
  previousAmount,
}: HardCommitPanelProps) {
  const colors = useThemeColors();
  const keyboardVisible = useKeyboardVisible();
  const amountValid = hardCommitAmount && parseFloat(hardCommitAmount) > 0;

  if (!visible) return null;

  return (
    <Animated.View
      entering={FadeIn}
      className="absolute inset-0"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      {/* First tap dismisses the keyboard and keeps the sheet; only a second
          tap closes. These sheets hold free-text commitment notes and a
          conclusion rationale — closing on the first tap discarded them. */}
      <Pressable
        className="flex-1"
        accessibilityLabel={keyboardVisible ? 'Dismiss keyboard' : 'Close'}
        onPress={() => {
          if (keyboardVisible) {
            Keyboard.dismiss();
            return;
          }
          onClose();
        }}
      />
      <KeyboardAvoidingView behavior={isIOS ? 'padding' : 'height'}>
      <Animated.View
        entering={FadeInUp.springify()}
        className="rounded-t-3xl p-5"
        style={{ backgroundColor: colors.surface.primary }}
      >
        <View className="flex-row items-center gap-3 mb-4">
          <View className="w-10 h-10 rounded-full items-center justify-center" style={{ backgroundColor: colors.traffic.greenLight }}>
            <DollarSign size={22} color={colors.success} />
          </View>
          <View className="flex-1">
            <Text style={{ color: colors.text.primary }} className="text-lg font-bold">
              {isConversion ? 'Confirm Investment' : 'Investment Commitment'}
            </Text>
            <Text style={{ color: colors.text.tertiary }} className="text-xs">
              {isConversion ? 'Convert your soft commit to a firm commitment' : 'Enter your investment amount'}
            </Text>
          </View>
        </View>

        {isConversion && previousAmount !== undefined && (
          <View className="flex-row items-center gap-2 mb-4 px-3 py-2 rounded-lg" style={{ backgroundColor: `${colors.primary[600]}10` }}>
            <TrendingUp size={14} color={colors.primary[600]} />
            <Text style={{ color: colors.primary[600] }} className="text-sm">
              Previous soft commitment: {formatCurrency(previousAmount)}
            </Text>
          </View>
        )}

        <Text style={{ color: colors.text.secondary }} className="text-sm font-medium mb-2">Final Amount</Text>
        <View className="flex-row items-center gap-2 mb-4">
          <View className="flex-row items-center flex-1 rounded-xl px-4" style={{ backgroundColor: colors.surface.secondary, borderWidth: 2, borderColor: colors.success }}>
            <Text style={{ color: colors.text.primary }} className="text-lg font-semibold mr-1">$</Text>
            <TextInput
              className="flex-1 py-3 text-lg"
              style={{ color: colors.text.primary }}
              value={hardCommitAmount}
              onChangeText={onSetHardCommitAmount}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor={colors.text.tertiary}
            />
            <Text style={{ color: colors.text.tertiary }} className="text-base font-medium">K</Text>
          </View>
        </View>

        <View className="flex-row gap-3">
          <Pressable
            onPress={onClose}
            className="flex-1 py-4 rounded-xl items-center"
            style={{ backgroundColor: colors.surface.secondary }}
          >
            <Text className="font-medium" style={{ color: colors.text.secondary }}>Skip</Text>
          </Pressable>
          <AnimatedPressable
            onPress={onSubmit}
            disabled={!amountValid}
            className="flex-1 py-4 rounded-xl items-center"
            style={{ backgroundColor: amountValid ? colors.success : colors.surface.tertiary }}
          >
            <Text className="font-semibold" style={{ color: amountValid ? 'white' : colors.text.tertiary }}>
              Confirm
            </Text>
          </AnimatedPressable>
        </View>
      </Animated.View>
      </KeyboardAvoidingView>
    </Animated.View>
  );
}
